<template>
    <div class="mx-auto max-w-[1232px] px-3 py-4 sm:px-6 sm:pb-8 sm:pt-10">
        <!-- Plugin Not Enabled State -->
        <div v-if="!props.teamPluginEnabled" class="bg-white shadow-sm border border-border p-8">
            <div class="flex items-start gap-6">
                <!-- Warning Icon -->
                <div class="shrink-0">
                    <svg
                        class="w-10 h-10 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                    </svg>
                </div>

                <!-- Content -->
                <div class="flex-1">
                    <H3 class="text-lg font-semibold text-foreground mb-2">
                        Plugin not enabled
                    </H3>
                    <P class="text-new-neutral-600 mb-4">
                        In order to use TOPOL.io Plugin you need to enable it for your account first.
                    </P>
                    <P class="text-sm text-muted-foreground mb-4">
                        TOPOL.io Plugin is a version of TOPOL.io editor which can be embedded into your platform.
                        You will get a white label version of email template editor that can be used by your users.
                    </P>
                    <P class="text-sm text-muted-foreground mb-6">
                        <a
                            href="https://docs.topol.io/"
                            target="_blank"
                            class="text-new-blue-600 hover:text-new-blue-700 font-medium hover:underline"
                        >
                            Check out our documentation
                        </a>
                        how to create API Token, embed plugin, setup custom data storage and more.
                    </P>
                    <Button
                        variant="default"
                        size="lg"
                        @click="enablePlugin"
                    >
                        <svg
                            class="w-5 h-5 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M13 10V3L4 14h7v7l9-11h-7z"
                            />
                        </svg>
                        Setup plugin
                    </Button>
                </div>
            </div>
        </div>

        <!-- Tokens Table -->
        <DataTableV2
            v-else
            title="API Tokens"
            :headers="headers"
            :items="props.tokens"
            :row-href="(row) => route('plugin.api-tokens.usage', { apiToken: row.id })"
            searchable
        >
            <template #item-name="{ row }">
                <div class="flex flex-col">
                    <div class="font-semibold text-sm text-foreground">{{ row.name }}</div>
                    <div class="text-xs text-muted-foreground mt-0.5">
                        {{ formatDomains(row.domains) }}
                    </div>
                </div>
            </template>

            <template #item-usage="{ row }">
                <Deferred data="tokenUsage">
                    <template #fallback>
                        <div class="h-4 w-12 animate-pulse bg-new-neutral-200"></div>
                    </template>
                    {{ formatBytes(props.tokenUsage[row.id] || 0) }}
                </Deferred>
            </template>

            <template #item-storage="{ row }">
                <div class="flex items-center gap-2">
                    <!-- Custom storage with valid provider -->
                    <template v-if="row.settings?.storage_provider && !row.settings.storage_provider.includes('disabled')">
                        <img
                            v-if="getStorageProvider(row.settings.storage_provider)"
                            :src="getStorageProvider(row.settings.storage_provider)?.img_mini"
                            :alt="getStorageProvider(row.settings.storage_provider)?.name"
                            class="h-6 object-contain"
                        />
                    </template>

                    <!-- Default storage -->
                    <span v-else class="text-muted-foreground">Default</span>

                    <!-- Warning: Storage blocked (default storage + bucket_blocked_at) -->
                    <TooltipComponent v-if="!row.settings?.storage_provider && row.bucket_blocked_at">
                        <TooltipTrigger>
                            <ExclamationTriangleIcon class="h-4 w-4 text-new-orange-500" />
                        </TooltipTrigger>
                        <TooltipContent>Storage blocked</TooltipContent>
                    </TooltipComponent>

                    <!-- Warning: Custom storage disabled -->
                    <TooltipComponent v-if="row.settings?.storage_provider?.includes('disabled') && !row.bucket_blocked_at">
                        <TooltipTrigger>
                            <ExclamationTriangleIcon class="h-4 w-4 text-new-orange-500" />
                        </TooltipTrigger>
                        <TooltipContent>Custom storage disabled</TooltipContent>
                    </TooltipComponent>
                </div>
            </template>

            <template #item-status="{ row }">
                <StatusBadge :status="row.last_used_at && !row.expires_at ? 'active' : 'inactive'" />
            </template>

            <template #item-created_at="{ data }">
                <div v-if="data">
                    {{ format(new Date(data as string), 'MMM d, yyyy ') }}
                </div>
            </template>

            <template #item-actions="{ row }">
                <a
                    href="#"
                    class="text-new-blue-600 hover:text-new-blue-700 hover:underline font-medium text-sm"
                    @click.stop.prevent="copyKey(row)"
                >
                    Copy key
                </a>
            </template>

            <template #toolbar>
                <TooltipComponent v-if="!canCreateApiToken" :delay-duration="150">
                    <TooltipTrigger as-child>
                        <span tabindex="0">
                            <Button variant="default" size="lg" disabled>
                                <Lock class="mr-2 size-4" />
                                New API Token
                            </Button>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        You need an active plugin subscription or trial to create
                        API tokens.
                    </TooltipContent>
                </TooltipComponent>
                <Button v-else variant="default" size="lg" @click="createToken">
                    + New API Token
                </Button>
            </template>

            <template #empty>
                <div class="flex flex-col items-center justify-center gap-5 py-20">
                    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 80H0V60.002H10V80ZM29.999 80H19.999V60.002H29.999V80ZM59.999 80H49.999V60.002H59.999V80ZM79.999 80H69.999V60.002H79.999V80ZM19.999 50H29.999V30.002H49.999V50H59.999V30.002H69.999V50H79.999V60H69.999V50.002H59.999V60H49.999V50.002H29.999V60H19.999V50.002H10V60H0V50H9.99902V30.002H19.999V50ZM10 20H19.999V10.002H59.999V20H69.999V10.002H79.999V20.002H69.999V30H59.999V20.002H19.999V30H9.99902V20.002H0V10.002H10V20ZM10 10H0V0H10V10ZM79.999 10H69.999V0H79.999V10Z" fill="#E5E5E5"/>
                    </svg>

                    <!-- Text content -->
                    <div class="flex flex-col items-center gap-1.5">
                        <p class="text-xl text-new-neutral-800">No tokens yet.</p>
                        <p class="text-sm text-muted-foreground">
                            Create a new API token or learn how to
                            <a
                                href="https://docs.topol.io/guide/getting-started.html"
                                target="_blank"
                                class="font-medium text-new-blue-500 underline decoration-solid hover:text-new-blue-600"
                            >
                                migrate to Topol</a>
                        </p>
                    </div>
                </div>
            </template>
        </DataTableV2>
    </div>
</template>

<script setup lang="ts">
import { DataTableV2 } from '@/components/ui/data-table-v2'
import type { IDataTableHeader, IPaginationData } from '@/components/ui/data-table-v2'
import { Button } from '@/components/ui/button'
import StatusBadge from './components/StatusBadge.vue'
import DefaultLayoutV2 from '@/Layouts/DefaultV2.vue'
import { Deferred, router, usePage } from '@inertiajs/vue3'
import { route } from 'ziggy-js'
import { computed, PropType } from 'vue'
import ISubscription from '@/types/Subscription/ISubscription'
import IToken from '@/types/Plugin/IToken'
import { format } from 'date-fns'
import { useClipboard } from '@vueuse/core'
import { toast } from 'vue-sonner'
import { allProviders } from '@/Pages/PluginSettings/data/storageProviders'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { TooltipComponent, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps'
import { Lock } from 'lucide-vue-next'

defineOptions({
    layout: DefaultLayoutV2,
})

const props = defineProps({
    teamPluginEnabled: {
        type: Boolean,
        default: false,
    },
    tokens: {
        type: Object as PropType<IPaginationData<IToken>>,
        default: () => ({}),
    },
    pluginSubscription: {
        type: Object as PropType<ISubscription>,
        default: () => null,
    },
    features: {
        type: Object as PropType<Record<string, boolean>>,
        default: () => ({}),
    },
    hasStorageSettingsEnabled: {
        type: Boolean,
        default: false,
    },
    tokenUsage: {
        type: Object as PropType<Record<string, number>>,
        default: () => ({}),
    },
    canCreateApiToken: {
        type: Boolean,
        default: false,
    },
});

const headers: IDataTableHeader[] = [
    { id: 'name', text: 'Name', sortable: true, align: 'left' },
    { id: 'usage', text: 'Usage', sortable: false, align: 'center', width: '120px' },
    { id: 'storage', text: 'Storage', sortable: true, align: 'center' },
    { id: 'status', text: 'Status', sortable: true, sortableId: 'last_used_at', align: 'center' },
    { id: 'created_at', text: 'Created', sortable: true, align: 'center' },
    { id: 'actions', text: 'Public API Key', sortable: false, align: 'right', headerClass: 'text-foreground' },
]

const clipboard = useClipboard()

const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'kB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(0)) + ' ' + sizes[i]
}

const getStorageProvider = (key: string | undefined) => {
    if (!key) return null
    return allProviders.find(p => p.key === key)
}

const formatDomains = (domains: string | null): string => {
    if (!domains) {
        return 'No domains'
    }

    const domainList = domains.split(',').map(d => d.trim()).filter(d => d)

    if (domainList.length === 0) {
        return 'No domains'
    }

    // Show a single domain, then collapse the rest into "(and X more)"
    const maxVisible = 1

    if (domainList.length <= maxVisible) {
        return domainList.join('  ')
    }

    const visibleDomains = domainList.slice(0, maxVisible)
    const remaining = domainList.length - maxVisible

    return `${visibleDomains.join('  ')} (and ${remaining} more)`
}

const copyKey = (token: IToken) => {
    if (token.token) {
        clipboard.copy(token.token)
        toast.success('API Key copied to clipboard!')
    } else {
        toast.error('No API key available to copy')
    }
}

const createToken = () => {
    router.post(
        route('plugin.api-tokens.store'),
        {
            name: 'Untitled',
            domains: null,
        },
        {
            preserveScroll: true,
            onSuccess: (page) => {
                // store() redirects to the token detail page on success, but bounces
                // unauthorized users to billing (also a 2xx visit). Only celebrate when
                // we actually landed on the token detail page.
                if (page.component === 'PluginSettingsV2/show') {
                    toast.success('API Token created successfully!')
                }
            },
            onError: (errors) => {
                const errorMessage = Object.values(errors)[0] as string || 'Failed to create API token'
                toast.error(errorMessage)
            },
        }
    )
}

const enablePlugin = () => {
    const globalProps = computed(() => usePage<ITopolPageProps>().props);
    if (
        props.pluginSubscription ||
        (globalProps.value.user.plugin_trial_ends_at &&
            new Date(globalProps.value.user.plugin_trial_ends_at) > new Date())
    ) {
        router.post(
            route('plugin.enable'),
            {},
            {
                preserveScroll: true,
                onSuccess: () => {
                     toast.success('Plugin enabled successfully!')
                },
                onError: () => {
                    toast.error('Failed to enable plugin. Please try again.')
                },
            }
        );
    } else router.get(route('billing.plugin-subscriptions.index'));
};
</script>
